import React from 'react'
import { Link } from 'react-router-dom'
import { Mail, MapPin, Linkedin, Twitter } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const Footer = () => {
  const { t } = useTranslation(['footer', 'common'])
  const currentYear = new Date().getFullYear()

  const footerLinks = {
    produto: [
      { label: t('footer:sections.product.links.features'), to: '/funcionalidades' },
      { label: t('footer:sections.product.links.pricing'), to: '/planos' },
      { label: 'Blog', to: '/blog' },
      { label: 'RH e DP', to: '/rh' },
      { label: 'Advocacia', to: '/advocacia' },
      { label: 'Imobiliária', to: '/imobiliaria' },
      { label: 'Saúde', to: '/saude' },
      { label: 'ICP-Brasil', to: '/icp-brasil' },
    ],
    empresa: [
      { label: t('footer:sections.company.links.about'), to: '/sobre' },
      { label: t('footer:sections.company.links.contact'), to: '/contato' },
    ],
    desenvolvedores: [
      { label: t('footer:sections.developers.links.api_docs'), href: 'https://temporiza.readme.io/', external: true },
    ],
    legal: [
      { label: t('footer:sections.legal.links.terms'), to: '/termos' },
      { label: t('footer:sections.legal.links.privacy'), to: '/privacidade' },
      { label: t('footer:sections.legal.links.lgpd'), to: '/lgpd' },
    ],
  }

  return (
    <footer className="bg-secondary-900 text-white">
      {/* Main Footer Content */}
      <div className="container-institutional py-8 md:py-12 lg:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6 md:gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center space-x-2.5 mb-4">
              <img
                src="/logo-temporiza.png"
                alt="Temporiza"
                className="w-8 h-8 object-contain"
              />
              <span className="text-xl font-bold tracking-tight">Temporiza</span>
            </Link>

            <p className="text-secondary-300 mb-6 max-w-sm">
              {t('footer:company.description')}
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-primary-400" />
                <a
                  href={`mailto:${t('common:company.email')}`}
                  className="text-secondary-300 hover:text-white transition-colors"
                >
                  {t('common:company.email')}
                </a>
              </div>

              <div className="flex items-center space-x-3">
                <MapPin className="w-4 h-4 text-primary-400" />
                <span className="text-secondary-300">
                  {t('common:company.location')}
                </span>
              </div>
            </div>
          </div>

          {/* Links Columns */}
          <div>
            <h3 className="font-semibold mb-4">{t('footer:sections.product.title')}</h3>
            <ul className="space-y-3">
              {footerLinks.produto.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-secondary-300 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">{t('footer:sections.company.title')}</h3>
            <ul className="space-y-3">
              {footerLinks.empresa.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-secondary-300 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">{t('footer:sections.developers.title')}</h3>
            <ul className="space-y-3">
              {footerLinks.desenvolvedores.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-secondary-300 hover:text-white transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">{t('footer:sections.legal.title')}</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-secondary-300 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="border-t border-secondary-700 pt-8 mt-12">
          <div className="max-w-md">
            <h3 className="font-semibold mb-4">
              {t('footer:newsletter.title')}
            </h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder={t('footer:newsletter.placeholder')}
                className="flex-1 px-4 py-3 bg-secondary-800 border border-secondary-700 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <button className="btn btn-primary px-6 w-full sm:w-auto whitespace-nowrap">
                {t('footer:newsletter.button')}
              </button>
            </div>
            <p className="text-xs text-secondary-400 mt-2">
              {t('footer:newsletter.disclaimer')}
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-secondary-700">
        <div className="container-institutional py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            {/* Copyright */}
            <p className="text-secondary-400 text-sm">
              {t('footer:bottom.copyright', { year: currentYear })}
            </p>

            {/* Social Links */}
            <div className="flex items-center space-x-4">
              <a
                href={import.meta.env.VITE_LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary-400 hover:text-primary-400 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>

              <a
                href={import.meta.env.VITE_TWITTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary-400 hover:text-primary-400 transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
            </div>

            {/* Certifications */}
            <div className="flex items-center space-x-4">
              <span className="text-xs text-secondary-400">
                {t('footer:bottom.certifications.icp')}
              </span>
              <span className="text-xs text-secondary-400">
                {t('footer:bottom.certifications.lgpd')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
